import { useIsMobile } from "@/hooks/use-mobile";
import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { routes } from "@/routes/utils";
import { useAddGeoRegionsMutation, useUpdateGeoRegionsMutation } from "@/services/masterData";
import useGetGeoRegionDetailQuery from "@/services/masterData/georegions/useGeoRegionDetailMutation";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { DESCRIPTION, NAME } from "../consts";
import { formValues, schema } from "./validations";

const useAddEdit = () => {
  const { action,id } = useParams();
  const navigate = useNavigate();
  const isView = action === routes.view;
  const isEdit = action === routes.edit;
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
const [initialDetailPayload, setInitialDetailPayload] = useState<Record<
    string,
    any
  > | null>(null);

const { data: detailRes, isPending: isDetailLoading } =
    useGetGeoRegionDetailQuery(
      isEdit || isView ? String(id) : undefined
    );

  const form = useForm<formValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      [NAME]: "",
      [DESCRIPTION]: "",
    },
  });

   useEffect(() => {
      if ((isEdit || isView) && detailRes?.data) {
        const d = detailRes.data;
        form.reset({
          [NAME]: String(d?.name ?? ""),
          [DESCRIPTION]: d?.description ? String(d?.description) : "",
        });
        // Store normalized initial payload for diffing on update
        setInitialDetailPayload({
          name: String(d?.name ?? ""),
          description: d?.description ? String(d?.description) : undefined,
        });
      }
    }, [detailRes, isEdit, isView, form]);

  const requiredFields = getRequiredFields(schema);

  const { mutateAsync: updateGeoRegion, isPending: isUpdating } =
    useUpdateGeoRegionsMutation({
      onSuccess: () => {
        toast.success("Geo Region updated successfully");
        onBackClick();
      },
      onError: (error: any) => {
        toast.error(
          error.response.data?.issues?.message || error.response.data.message
            || "Failed to update geo region"
        );
      },
    });

  const { mutate: addGeoRegion, isPending, isError, error, isSuccess } =
    useAddGeoRegionsMutation({
      onSuccess: () => {
        toast.success("Geographical region added successfully");
        queryClient.invalidateQueries({ queryKey: ["geoRegions"] });
        navigate(`/${routes.admin}/${routes.masterData}`);
      },
      onError: (err:any) => {
        toast.error(err.response.data?.issues?.message ||err.response.data.message || "Failed to add geographical region");
      },
    });

  const onBackClick = () => {
    navigate(`/${routes.admin}/${routes.masterData}`);
  };

  const onSubmit = async (data: formValues) => {
    let _data = removeEmptyFields(data);
    _data = removeEmptyFields(_data);
    if (isEdit && id) {
      await updateGeoRegion({ id: String(id), data: _data });
    }else{
    await addGeoRegion({ data: _data });
    }
    queryClient.invalidateQueries({ queryKey: ["geoRegions"] });
  };

  return {
    form,
    onSubmit,
    requiredFields,
    isView,
    onCancel: onBackClick,
    isMobile,
    action,
    isEdit,
    isPending,
    isError,
    error,
    isSuccess,
  };
};

export default useAddEdit;